import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { db } from '../firebase';

const formatCurrency = (amount) =>
  Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function formatLocalDateOnly(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getDefaultSpendRange() {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
  return {
    startDate: formatLocalDateOnly(start),
    endDate: formatLocalDateOnly(end),
  };
}

function monthKeysBetween(startDate, endDate) {
  const start = new Date(`${String(startDate).slice(0, 10)}T00:00:00`);
  const end = new Date(`${String(endDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= last) {
    months.push({
      key: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`,
      label: cursor.toLocaleString(undefined, { month: 'short', year: 'numeric' }),
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

function parseToDate(value) {
  if (!value) return null;
  try {
    if (typeof value.toDate === 'function') {
      const d = value.toDate();
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof value.seconds === 'number') {
      const d = new Date(value.seconds * 1000);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
}

function itemAmount(data) {
  const n = Number(data?.paymentAmount ?? data?.totalPrice ?? data?.totalBudget ?? data?.budget ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function itemUserId(data) {
  return String(data?.userUid || data?.userId || data?.createdBy || '').trim();
}

function isWarmup(id, data) {
  const sid = String(id || data?.id || '');
  return !!(data?.isWarmup || data?.iswarmup || sid.startsWith('warmup-'));
}

function isCanceledStatus(status) {
  const s = String(status || '').toLowerCase();
  return s === 'canceled' || s === 'cancelled' || s === 'rejected' || s === 'void' || s === 'voided';
}

function normalizePaymentStatus(raw) {
  const s = String(raw || '').toLowerCase();
  if (s === 'captured' || s === 'capturedpendingsettlement') return 'captured';
  if (s === 'authorized' || s === 'authorizedpendingcapture' || s === 'auth_only' || s === 'authorize_only') {
    return 'authorized';
  }
  if (s === 'refunded' || s === 'refund') return 'refunded';
  if (s === 'voided' || s === 'void') return 'voided';
  return s || 'none';
}

function itemPaymentStatus(data) {
  const fromStatus = normalizePaymentStatus(data.paymentStatus);
  if (fromStatus !== 'none') return fromStatus;
  const type = String(data.paymentType || '').toLowerCase();
  if (type === 'authorize_only' || type === 'auth_only') return 'authorized';
  return 'none';
}

function paymentLabel(status) {
  if (status === 'captured') return 'Captured';
  if (status === 'authorized') return 'Authorized';
  if (status === 'refunded') return 'Refunded';
  if (status === 'voided') return 'Voided';
  if (status === 'none') return 'No payment';
  return status;
}

function userDisplayName(user, fallbackId) {
  if (!user) return fallbackId || 'Unknown';
  return user.name || user.displayName || user.userName || user.email || fallbackId || 'Unknown';
}

function normEmail(email) {
  return String(email || '').trim().toLowerCase();
}

function resolveAgency(userId, user, lookups) {
  const orgId = String(user?.organizationId || user?.orgId || '').trim();
  if (orgId && lookups.orgsById[orgId]) {
    const org = lookups.orgsById[orgId];
    return { key: `org:${org.id}`, name: org.name || org.companyName || org.id };
  }
  if (userId && lookups.orgsByAdmin[userId]) {
    const org = lookups.orgsByAdmin[userId];
    return { key: `org:${org.id}`, name: org.name || org.companyName || org.id };
  }
  const email = normEmail(user?.email);
  if (email && lookups.orgsByEmail[email]) {
    const org = lookups.orgsByEmail[email];
    return { key: `org:${org.id}`, name: org.name || org.companyName || org.id };
  }

  const dspId = String(user?.dspInstanceId || user?.dspInstance || '').trim();
  if (dspId && lookups.dspsById[dspId]) {
    const dsp = lookups.dspsById[dspId];
    return {
      key: `dsp:${dsp.id}`,
      name: dsp.dspInstanceName || dsp.name || dsp.companyName || dsp.id,
    };
  }
  if (userId && lookups.dspsByAdmin[userId]) {
    const dsp = lookups.dspsByAdmin[userId];
    return {
      key: `dsp:${dsp.id}`,
      name: dsp.dspInstanceName || dsp.name || dsp.companyName || dsp.id,
    };
  }

  const company = String(user?.companyName || '').trim();
  if (company) {
    return { key: `company:${company.toLowerCase()}`, name: company };
  }

  if (!userId) return { key: 'unknown', name: 'Unknown' };
  return {
    key: `user:${userId}`,
    name: userDisplayName(user, 'Independent'),
  };
}

const emptyAgg = () => ({
  campaignAmount: 0,
  takeoverAmount: 0,
  capturedAmount: 0,
  authorizedAmount: 0,
  campaignCount: 0,
  takeoverCount: 0,
  capturedCount: 0,
  authorizedCount: 0,
  items: [],
  userIds: new Set(),
});

function addItemToAgg(agg, item) {
  agg.items.push(item);
  if (item.userId) agg.userIds.add(item.userId);
  if (item.channel === 'campaign') {
    agg.campaignAmount += item.amount;
    agg.campaignCount += 1;
  } else {
    agg.takeoverAmount += item.amount;
    agg.takeoverCount += 1;
  }
  if (item.paymentStatus === 'captured') {
    agg.capturedAmount += item.amount;
    agg.capturedCount += 1;
  } else if (item.paymentStatus === 'authorized') {
    agg.authorizedAmount += item.amount;
    agg.authorizedCount += 1;
  }
}

const RevenueByCustomer = () => {
  const [range, setRange] = useState(getDefaultSpendRange);
  const [allTime, setAllTime] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState('booked');
  const [channelFilter, setChannelFilter] = useState('all');
  const [groupBy, setGroupBy] = useState('user');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [fetched, setFetched] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [detailView, setDetailView] = useState('grouped');

  const loadSpenders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [campaignsSnap, ordersSnap, usersSnap, orgsSnap, dspSnap] = await Promise.all([
        getDocs(collection(db, 'campaigns')),
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'organizations')).catch(() => ({ docs: [] })),
        getDocs(collection(db, 'dspInstances')).catch(() => ({ docs: [] })),
      ]);

      const usersById = {};
      usersSnap.docs.forEach((d) => {
        usersById[d.id] = { id: d.id, ...d.data() };
      });

      const orgsById = {};
      const orgsByAdmin = {};
      const orgsByEmail = {};
      orgsSnap.docs.forEach((d) => {
        const org = { id: d.id, ...d.data() };
        orgsById[d.id] = org;
        if (org.adminUserId) orgsByAdmin[org.adminUserId] = org;
        const email = normEmail(org.userEmail);
        if (email) orgsByEmail[email] = org;
      });

      const dspsById = {};
      const dspsByAdmin = {};
      dspSnap.docs.forEach((d) => {
        const dsp = { id: d.id, ...d.data() };
        dspsById[d.id] = dsp;
        if (dsp.adminUserId) dspsByAdmin[dsp.adminUserId] = dsp;
      });

      const lookups = { orgsById, orgsByAdmin, orgsByEmail, dspsById, dspsByAdmin };

      const toItem = (id, data, channel) => {
        const userId = itemUserId(data);
        const user = userId ? usersById[userId] : null;
        const agency = resolveAgency(userId, user, lookups);
        const date =
          parseToDate(data.paymentTimestamp) ||
          parseToDate(data.date) ||
          parseToDate(data.startDate) ||
          parseToDate(data.createdAt) ||
          parseToDate(data.updatedAt);
        const name =
          channel === 'campaign'
            ? data.name || data.campaignName || id
            : data.signName || data.name || id;
        return {
          id,
          channel,
          name,
          amount: itemAmount(data),
          paymentStatus: itemPaymentStatus(data),
          status: data.status || '—',
          date,
          userId: userId || '',
          userName: userDisplayName(user, userId || 'Unknown'),
          userEmail: user?.email || data.email || '',
          agencyKey: agency.key,
          agencyName: agency.name,
          href: channel === 'campaign' ? `/campaigns/${id}` : `/takeovers/${id}`,
        };
      };

      const items = [];
      campaignsSnap.docs.forEach((d) => {
        const data = d.data() || {};
        if (isWarmup(d.id, data)) return;
        if (isCanceledStatus(data.status)) return;
        items.push(toItem(d.id, data, 'campaign'));
      });
      ordersSnap.docs.forEach((d) => {
        const data = d.data() || {};
        if (isWarmup(d.id, data)) return;
        if (isCanceledStatus(data.status)) return;
        items.push(toItem(d.id, data, 'takeover'));
      });

      setLineItems(items);
      setFetched(true);
    } catch (e) {
      setLineItems([]);
      setError(e?.message || 'Failed to load campaigns and takeovers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSpenders();
  }, [loadSpenders]);

  const applyPreset = (preset) => {
    const end = new Date();
    if (preset === 'all') {
      setAllTime(true);
      return;
    }
    setAllTime(false);
    let start;
    if (preset === '30d') {
      start = new Date(end);
      start.setDate(end.getDate() - 29);
    } else if (preset === '3m') start = new Date(end.getFullYear(), end.getMonth() - 2, 1);
    else if (preset === 'ytd') start = new Date(end.getFullYear(), 0, 1);
    else start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
    setRange({
      startDate: formatLocalDateOnly(start),
      endDate: formatLocalDateOnly(end),
    });
  };

  const rangeItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    const start = !allTime && range.startDate ? new Date(`${range.startDate}T00:00:00`) : null;
    const end = !allTime && range.endDate ? new Date(`${range.endDate}T23:59:59`) : null;

    return lineItems.filter((item) => {
      if (channelFilter !== 'all' && item.channel !== channelFilter) return false;

      if (start || end) {
        if (!item.date) return false;
        if (start && item.date < start) return false;
        if (end && item.date > end) return false;
      }

      if (!q) return true;
      const hay = [
        item.name,
        item.id,
        item.userName,
        item.userEmail,
        item.userId,
        item.agencyName,
        item.status,
        item.paymentStatus,
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [lineItems, search, allTime, range, channelFilter]);

  const filteredItems = useMemo(() => {
    return rangeItems.filter((item) => {
      if (paymentFilter === 'booked') {
        return item.paymentStatus === 'captured' || item.paymentStatus === 'authorized';
      }
      if (paymentFilter === 'captured' || paymentFilter === 'authorized') {
        return item.paymentStatus === paymentFilter;
      }
      return item.amount > 0;
    });
  }, [rangeItems, paymentFilter]);

  const groupedRows = useMemo(() => {
    const map = {};
    filteredItems.forEach((item) => {
      const key = groupBy === 'agency' ? item.agencyKey : item.userId || `unknown:${item.id}`;
      if (!map[key]) {
        map[key] = {
          key,
          ...emptyAgg(),
          label: groupBy === 'agency' ? item.agencyName : item.userName,
          sublabel: groupBy === 'agency' ? '' : item.userEmail,
          agencyName: item.agencyName,
        };
      }
      const row = map[key];
      addItemToAgg(row, item);
      if (groupBy === 'user') {
        row.label = item.userName || row.label;
        row.sublabel = item.userEmail || row.sublabel;
        row.agencyName = item.agencyName;
      } else if (item.agencyName && item.agencyName !== 'Independent') {
        row.label = item.agencyName;
      }
    });

    return Object.values(map)
      .map((row) => ({
        ...row,
        total: row.campaignAmount + row.takeoverAmount,
        userCount: row.userIds.size,
      }))
      .sort((a, b) => b.total - a.total);
  }, [filteredItems, groupBy]);

  const totals = useMemo(() => {
    return filteredItems.reduce(
      (acc, item) => {
        acc.total += item.amount;
        if (item.channel === 'campaign') {
          acc.campaign += item.amount;
          acc.campaignCount += 1;
        } else {
          acc.takeover += item.amount;
          acc.takeoverCount += 1;
        }
        if (item.paymentStatus === 'captured') acc.captured += item.amount;
        if (item.paymentStatus === 'authorized') {
          acc.authorized += item.amount;
          acc.authorizedCount += 1;
        }
        if (item.userId) acc.userIds.add(item.userId);
        acc.agencyKeys.add(item.agencyKey);
        return acc;
      },
      {
        total: 0,
        campaign: 0,
        takeover: 0,
        captured: 0,
        authorized: 0,
        campaignCount: 0,
        takeoverCount: 0,
        authorizedCount: 0,
        userIds: new Set(),
        agencyKeys: new Set(),
      }
    );
  }, [filteredItems]);

  const paymentSnapshot = useMemo(() => {
    return rangeItems.reduce(
      (acc, item) => {
        if (item.paymentStatus === 'captured') acc.captured += item.amount;
        if (item.paymentStatus === 'authorized') {
          acc.authorized += item.amount;
          acc.authorizedCount += 1;
        }
        return acc;
      },
      { captured: 0, authorized: 0, authorizedCount: 0 }
    );
  }, [rangeItems]);

  const campaignShare = totals.total > 0 ? (totals.campaign / totals.total) * 100 : 0;
  const takeoverShare = totals.total > 0 ? (totals.takeover / totals.total) * 100 : 0;

  const monthlyChannel = useMemo(() => {
    const startDate = allTime
      ? filteredItems.reduce((min, item) => {
          if (!item.date) return min;
          const key = formatLocalDateOnly(item.date);
          return !min || key < min ? key : min;
        }, '')
      : range.startDate;
    const endDate = allTime
      ? filteredItems.reduce((max, item) => {
          if (!item.date) return max;
          const key = formatLocalDateOnly(item.date);
          return !max || key > max ? key : max;
        }, '')
      : range.endDate;
    if (!startDate || !endDate) return [];
    const months = monthKeysBetween(startDate, endDate);
    const map = {};
    months.forEach((m) => {
      map[m.key] = { month: m.label, campaigns: 0, takeovers: 0, authorized: 0 };
    });
    filteredItems.forEach((item) => {
      if (!item.date) return;
      const key = `${item.date.getFullYear()}-${String(item.date.getMonth() + 1).padStart(2, '0')}`;
      if (!map[key]) return;
      if (item.channel === 'campaign') map[key].campaigns += item.amount;
      else map[key].takeovers += item.amount;
      if (item.paymentStatus === 'authorized') map[key].authorized += item.amount;
    });
    return months.map((m) => {
      const row = map[m.key];
      return {
        month: row.month,
        campaigns: +row.campaigns.toFixed(2),
        takeovers: +row.takeovers.toFixed(2),
        authorized: +row.authorized.toFixed(2),
      };
    });
  }, [filteredItems, allTime, range]);

  const authorizedItems = useMemo(
    () =>
      [...rangeItems.filter((item) => item.paymentStatus === 'authorized')].sort(
        (a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0)
      ),
    [rangeItems]
  );

  const sortedLineItems = useMemo(
    () =>
      [...filteredItems].sort(
        (a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0) || b.amount - a.amount
      ),
    [filteredItems]
  );

  const toggleExpand = (key) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  const paymentBadgeClass = (status) => {
    if (status === 'captured') return 'f-inv-pill f-inv-pill--paid';
    if (status === 'authorized') return 'f-inv-pill f-pay-pill--authorized';
    if (status === 'refunded' || status === 'voided') return 'f-inv-pill f-inv-pill--void';
    return 'f-inv-pill';
  };

  const renderItemTable = (items, emptyText) => {
    if (!items.length) {
      return (
        <div style={{ padding: 28, textAlign: 'center', color: 'var(--ms-text-secondary)', fontSize: 14 }}>
          {emptyText}
        </div>
      );
    }
    return (
      <div style={{ overflowX: 'auto' }}>
        <table className="f-table">
          <thead>
            <tr>
              <th>Channel</th>
              <th>Name</th>
              <th>Customer</th>
              <th>Agency</th>
              <th>Date</th>
              <th>Payment</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={`${item.channel}-${item.id}`}>
                <td>
                  <span className={`f-channel-pill f-channel-pill--${item.channel}`}>
                    {item.channel === 'campaign' ? 'Campaign' : 'Takeover'}
                  </span>
                </td>
                <td>
                  <Link to={item.href} className="f-spend-link">
                    {item.name}
                  </Link>
                  <div className="f-wallet-mono f-wallet-sub">{item.id}</div>
                </td>
                <td>
                  <div style={{ fontWeight: 600 }}>{item.userName}</div>
                  {item.userEmail ? <div className="f-wallet-sub">{item.userEmail}</div> : null}
                </td>
                <td>{item.agencyName}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {item.date ? item.date.toLocaleDateString() : '—'}
                </td>
                <td>
                  <span className={paymentBadgeClass(item.paymentStatus)}>
                    {paymentLabel(item.paymentStatus)}
                  </span>
                </td>
                <td style={{ fontWeight: 700 }}>${formatCurrency(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const rangeLabel = allTime ? 'All time' : `${range.startDate} → ${range.endDate}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="f-card" style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 600 }}>Customer spend</div>
            <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>
              Authorized + captured campaigns and takeovers, rolled up by user or agency
            </div>
          </div>
          <button type="button" className="f-button f-button-secondary" disabled={loading} onClick={loadSpenders}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="f-button f-button-secondary" onClick={() => applyPreset('30d')}>30d</button>
          <button type="button" className="f-button f-button-secondary" onClick={() => applyPreset('3m')}>3 mo</button>
          <button type="button" className="f-button f-button-secondary" onClick={() => applyPreset('12m')}>12 mo</button>
          <button type="button" className="f-button f-button-secondary" onClick={() => applyPreset('ytd')}>YTD</button>
          <button type="button" className="f-button f-button-secondary" onClick={() => applyPreset('all')}>All time</button>
          <input
            type="date"
            className="f-input"
            disabled={allTime}
            value={range.startDate}
            onChange={(e) => {
              setAllTime(false);
              setRange((prev) => ({ ...prev, startDate: e.target.value }));
            }}
          />
          <input
            type="date"
            className="f-input"
            disabled={allTime}
            value={range.endDate}
            onChange={(e) => {
              setAllTime(false);
              setRange((prev) => ({ ...prev, endDate: e.target.value }));
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="f-input" value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} style={{ minWidth: 180 }}>
            <option value="booked">Authorized + captured</option>
            <option value="captured">Captured only</option>
            <option value="authorized">Authorized only</option>
            <option value="all">All with amount</option>
          </select>
          <select className="f-input" value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)} style={{ minWidth: 150 }}>
            <option value="all">All channels</option>
            <option value="campaign">Campaigns</option>
            <option value="takeover">Takeovers</option>
          </select>
          <select className="f-input" value={groupBy} onChange={(e) => { setGroupBy(e.target.value); setExpandedKey(null); }} style={{ minWidth: 150 }}>
            <option value="user">Group by user</option>
            <option value="agency">Group by agency</option>
          </select>
          <input
            type="search"
            className="f-input"
            placeholder="Search name, email, agency, or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 240, flex: 1 }}
          />
        </div>
      </div>

      {error && (
        <div className="f-wallet-msg f-wallet-msg-err">{error}</div>
      )}

      <div className="f-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
        <div className="f-card f-stat-card" style={{ margin: 0 }}>
          <span className="f-stat-label">Captured</span>
          <span className="f-stat-value" style={{ color: '#107c10' }}>
            {loading ? '…' : `$${formatCurrency(paymentSnapshot.captured)}`}
          </span>
          <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>{rangeLabel}</div>
        </div>
        <div className="f-card f-stat-card" style={{ margin: 0 }}>
          <span className="f-stat-label">Authorized (pending)</span>
          <span className="f-stat-value" style={{ color: '#d97706' }}>
            {loading ? '…' : `$${formatCurrency(paymentSnapshot.authorized)}`}
          </span>
          <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
            {paymentSnapshot.authorizedCount} hold{paymentSnapshot.authorizedCount !== 1 ? 's' : ''}
          </div>
        </div>
        <div className="f-card f-stat-card" style={{ margin: 0 }}>
          <span className="f-stat-label">Campaigns</span>
          <span className="f-stat-value" style={{ color: '#0b7fd4' }}>
            {loading ? '…' : `$${formatCurrency(totals.campaign)}`}
          </span>
          <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
            {totals.campaignCount} campaign{totals.campaignCount !== 1 ? 's' : ''}
          </div>
        </div>
        <div className="f-card f-stat-card" style={{ margin: 0 }}>
          <span className="f-stat-label">Takeovers</span>
          <span className="f-stat-value" style={{ color: '#7c3aed' }}>
            {loading ? '…' : `$${formatCurrency(totals.takeover)}`}
          </span>
          <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
            {totals.takeoverCount} takeover{totals.takeoverCount !== 1 ? 's' : ''}
          </div>
        </div>
        <div className="f-card f-stat-card" style={{ margin: 0 }}>
          <span className="f-stat-label">{groupBy === 'agency' ? 'Agencies' : 'Customers'}</span>
          <span className="f-stat-value">
            {loading ? '…' : (groupBy === 'agency' ? totals.agencyKeys.size : totals.userIds.size)}
          </span>
          <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>
            ${formatCurrency(totals.total)} booked
          </div>
        </div>
      </div>

      <div className="f-card" style={{ padding: 20 }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>Channel mix</div>
        <div className="f-spend-mix" aria-hidden={!totals.total}>
          <div className="f-spend-mix-seg f-spend-mix-campaign" style={{ width: `${campaignShare}%` }} />
          <div className="f-spend-mix-seg f-spend-mix-takeover" style={{ width: `${takeoverShare}%` }} />
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, fontSize: 13 }}>
          <span>
            <span className="f-spend-dot" style={{ background: '#0b7fd4' }} />
            Campaigns {campaignShare.toFixed(0)}% · ${formatCurrency(totals.campaign)}
          </span>
          <span>
            <span className="f-spend-dot" style={{ background: '#7c3aed' }} />
            Takeovers {takeoverShare.toFixed(0)}% · ${formatCurrency(totals.takeover)}
          </span>
        </div>
        <div style={{ height: 260, marginTop: 16, position: 'relative' }}>
          {loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.7)', zIndex: 1, fontSize: 14, color: 'var(--ms-text-secondary)' }}>
              Loading Firestore…
            </div>
          )}
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyChannel}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#edebe9" />
              <XAxis dataKey="month" fontSize={12} tickLine={false} axisLine={false} interval={0} angle={-30} textAnchor="end" height={50} />
              <YAxis fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} width={72} />
              <Tooltip
                formatter={(value, name) => [
                  `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                  name,
                ]}
              />
              <Legend />
              <Bar dataKey="campaigns" stackId="ch" fill="#0b7fd4" name="Campaigns" radius={[0, 0, 0, 0]} />
              <Bar dataKey="takeovers" stackId="ch" fill="#7c3aed" name="Takeovers" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="f-card" style={{ padding: 0 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ms-border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>
            {detailView === 'grouped'
              ? (groupBy === 'agency' ? 'Revenue by agency' : 'Revenue by user')
              : 'All campaigns & takeovers'}
          </span>
          <div style={{ display: 'flex', border: '1px solid var(--ms-border)', borderRadius: 2, overflow: 'hidden' }}>
            <button
              type="button"
              className={`f-button ${detailView === 'grouped' ? '' : 'f-button-secondary'}`}
              style={{ border: 'none', borderRadius: 0, height: 32, fontSize: 12 }}
              onClick={() => setDetailView('grouped')}
            >
              Grouped
            </button>
            <button
              type="button"
              className={`f-button ${detailView === 'items' ? '' : 'f-button-secondary'}`}
              style={{ border: 'none', borderRadius: 0, height: 32, fontSize: 12 }}
              onClick={() => setDetailView('items')}
            >
              Line items
            </button>
          </div>
        </div>

        {loading && !fetched ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ms-text-secondary)' }}>Loading spenders…</div>
        ) : detailView === 'items' ? (
          renderItemTable(sortedLineItems, 'No campaigns or takeovers match these filters.')
        ) : groupedRows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ms-text-secondary)', fontSize: 14 }}>
            No spenders match these filters.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="f-table">
              <thead>
                <tr>
                  <th>{groupBy === 'agency' ? 'Agency' : 'Customer'}</th>
                  {groupBy === 'user' ? <th>Agency</th> : <th>Users</th>}
                  <th>Campaigns</th>
                  <th>Takeovers</th>
                  <th>Authorized</th>
                  <th>Captured</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {groupedRows.map((row) => {
                  const open = expandedKey === row.key;
                  return (
                    <React.Fragment key={row.key}>
                      <tr
                        className={open ? 'f-row-selected' : ''}
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleExpand(row.key)}
                      >
                        <td>
                          <div style={{ fontWeight: 600 }}>{row.label}</div>
                          {row.sublabel ? <div className="f-wallet-sub">{row.sublabel}</div> : null}
                          <div className="f-wallet-sub">
                            {row.campaignCount + row.takeoverCount} order{row.campaignCount + row.takeoverCount !== 1 ? 's' : ''}
                            {open ? ' · hide' : ' · view'}
                          </div>
                        </td>
                        <td>
                          {groupBy === 'user' ? row.agencyName : row.userCount}
                        </td>
                        <td>
                          ${formatCurrency(row.campaignAmount)}
                          <div className="f-wallet-sub">{row.campaignCount}</div>
                        </td>
                        <td>
                          ${formatCurrency(row.takeoverAmount)}
                          <div className="f-wallet-sub">{row.takeoverCount}</div>
                        </td>
                        <td style={{ color: row.authorizedAmount ? '#d97706' : undefined, fontWeight: row.authorizedAmount ? 600 : undefined }}>
                          ${formatCurrency(row.authorizedAmount)}
                        </td>
                        <td style={{ color: '#107c10' }}>${formatCurrency(row.capturedAmount)}</td>
                        <td style={{ fontWeight: 700 }}>${formatCurrency(row.total)}</td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={7} style={{ padding: 0, background: 'var(--ms-bg, #faf9f8)' }}>
                            {renderItemTable(row.items, 'No line items.')}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, background: 'var(--ms-bg-secondary, #f3f2f1)' }}>
                  <td>Total</td>
                  <td>{groupBy === 'agency' ? groupedRows.length : totals.userIds.size}</td>
                  <td>${formatCurrency(totals.campaign)}</td>
                  <td>${formatCurrency(totals.takeover)}</td>
                  <td style={{ color: '#d97706' }}>${formatCurrency(totals.authorized)}</td>
                  <td style={{ color: '#107c10' }}>${formatCurrency(totals.captured)}</td>
                  <td>${formatCurrency(totals.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="f-card" style={{ padding: 0 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ms-border-light)', fontWeight: 600, fontSize: 14 }}>
          Authorized campaigns & takeovers
          <span style={{ fontWeight: 500, color: 'var(--ms-text-secondary)', marginLeft: 8 }}>
            {authorizedItems.length} pending capture · ${formatCurrency(paymentSnapshot.authorized)}
          </span>
        </div>
        {loading && !fetched ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--ms-text-secondary)' }}>Loading…</div>
        ) : (
          renderItemTable(authorizedItems, 'No authorized campaigns or takeovers in this range.')
        )}
      </div>
    </div>
  );
};

export default RevenueByCustomer;
